import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users, HandCoins, CalendarCheck, HeartHandshake, UserPlus, Plus, ClipboardCheck, TrendingUp, Cake,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

import { ChangeNote } from "@/components/ChangeNote";

const COLORS = ["#2D5A27", "#D97706", "#0284C7", "#9A3412", "#84A98C"];
const money = (n) => `$${Number(n).toLocaleString()}`;

function StatCard({ icon: Icon, label, value, sub, tint, testid, note }) {
  return (
    <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7A8A78]">{label}</p>
          <p className="mt-3 font-serif text-4xl font-bold text-[#1C241B]">{value}</p>
          {sub && <p className="mt-1 text-sm text-[#526050]">{sub}</p>}
          {note}
        </div>
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: tint }}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-white border border-[#E5E0D8] shadow-md px-3 py-2 text-sm">
      <p className="font-medium text-[#1C241B]">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-[#F3EFEA] animate-pulse" />
        ))}
      </div>
    );
  }

  const finHidden = !!stats.finance_hidden;
  const quickActions = [
    { label: "Add Member", icon: UserPlus, to: "/app/members", testid: "quick-add-member" },
    ...(finHidden ? [] : [{ label: "Log Donation", icon: Plus, to: "/app/donations", testid: "quick-log-donation" }]),
    { label: "Record Attendance", icon: ClipboardCheck, to: "/app/attendance", testid: "quick-record-attendance" },
    { label: "Manage Ministries", icon: HeartHandshake, to: "/app/ministries", testid: "quick-ministries" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${finHidden ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-6`}>
        <StatCard testid="stat-card-total-members" icon={Users} label="Active Members"
          value={stats.active_members} sub={`${stats.total_members} total records`} tint="#2D5A27" />
        {!finHidden && <StatCard testid="stat-card-total-donations" icon={HandCoins} label="Year Total Income"
          value={money(stats.fy_total)} sub={`${stats.fy_label} · Apr to date`}
          note={<ChangeNote pct={stats.fy_change_pct} />} tint="#D97706" />}
        <StatCard testid="stat-card-attendance" icon={CalendarCheck} label="Avg Service Attendance"
          value={stats.avg_attendance} sub="Sunday services" tint="#0284C7" />
        <StatCard testid="stat-card-engagement" icon={HeartHandshake} label="Ministry Engagement"
          value={`${stats.engagement_rate}%`} sub={`${stats.total_ministries} active ministries`} tint="#9A3412" />
      </div>

      {/* Quick actions */}
      <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#7A8A78] mb-4">Quick Actions</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <Button key={a.label} variant="outline" data-testid={a.testid}
              onClick={() => navigate(a.to)}
              className="h-auto py-4 flex flex-col items-center gap-2 border-[#E5E0D8] hover:border-[#2D5A27] hover:bg-[#EAF2E8] text-[#1C241B]">
              <a.icon className="h-5 w-5 text-[#2D5A27]" />
              <span className="text-sm font-medium">{a.label}</span>
            </Button>
          ))}
        </div>
      </Card>

      {stats.upcoming_birthdays?.length > 0 && (
        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid="birthdays-card">
          <div className="flex items-center gap-2 mb-4">
            <Cake className="h-5 w-5 text-[#D97706]" />
            <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Upcoming Birthdays</h3>
            <span className="text-xs text-[#7A8A78]">next 30 days</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {stats.upcoming_birthdays.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-xl border border-[#E5E0D8] bg-[#FBF9F5] px-4 py-2.5">
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-semibold" style={{ backgroundColor: b.avatar_color }}>
                  {b.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1C241B]">{b.name}</p>
                  <p className="text-xs text-[#7A8A78]">{b.in_days === 0 ? "Today" : b.in_days === 1 ? "Tomorrow" : `in ${b.in_days} days`}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {!finHidden && <Card className="lg:col-span-2 p-6 bg-white border border-[#E5E0D8] shadow-sm" data-testid="giving-trends-card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl font-semibold text-[#1C241B]">Giving Trends</h3>
            <span className="flex items-center gap-1 text-xs font-medium text-[#2D5A27]">
              <TrendingUp className="h-4 w-4" /> {stats.trend_label} vs {stats.trend_prev_label}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={280} data-testid="giving-trends-chart">
            <AreaChart data={stats.donation_trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="gGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2D5A27" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2D5A27" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip content={<ChartTooltip formatter={money} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="prev" name={stats.trend_prev_label} stroke="#A8B5A6" strokeWidth={2} strokeDasharray="6 4" fill="none" />
              <Area type="monotone" dataKey="amount" name={stats.trend_label} stroke="#2D5A27" strokeWidth={3} fill="url(#gGreen)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>}

        <Card className={`p-6 bg-white border border-[#E5E0D8] shadow-sm ${finHidden ? "lg:col-span-3" : ""}`}>
          <h3 className="font-serif text-xl font-semibold text-[#1C241B] mb-6">Ministry Participation</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={stats.ministry_breakdown} dataKey="value" nameKey="name"
                innerRadius={55} outerRadius={90} paddingAngle={3}>
                {stats.ministry_breakdown.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip formatter={(v) => `${v} members`} />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm">
          <h3 className="font-serif text-xl font-semibold text-[#1C241B] mb-6">Attendance by Gathering</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats.attendance_trend} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="service" name="Sunday" stroke="#2D5A27" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="bible" name="Bible Study" stroke="#D97706" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="training" name="Training" stroke="#0284C7" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 bg-white border border-[#E5E0D8] shadow-sm">
          <h3 className="font-serif text-xl font-semibold text-[#1C241B] mb-6">Membership Growth</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.member_growth} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#7A8A78" }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatter={(v) => `${v} members`} />} />
              <Bar dataKey="members" name="Members" fill="#D97706" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
